import { useEffect, useState } from "react";
import { persistNotificationPreferences } from "../../../lib/notifications";

/**
 * Muted spaces/DMs. Persists them locally and to the server, tells the service worker which spaces to keep quiet
 * (muted plus hidden), and follows mute changes made elsewhere (the `echo:mutedSpaces` event from settings).
 */
export function useMutedSpaces(hiddenSpaces: Record<string, boolean>) {
  const [mutedSpaces, setMutedSpaces] = useState<Record<string, boolean>>(() => {
    try { const s = localStorage.getItem('mutedSpaces'); return s ? JSON.parse(s) : {}; } catch { return {}; }
  });
  useEffect(() => {
    try {
      // Persist explicit mutes only
      localStorage.setItem('mutedSpaces', JSON.stringify(mutedSpaces));
      persistNotificationPreferences({ mutedSpaces, hiddenSpaces }).catch(() => {});
      // Build effective mute set = muted ∪ hidden (vaulted spaces are always quiet)
      const effective: Record<string, boolean> = { ...mutedSpaces };
      for (const [id, hidden] of Object.entries(hiddenSpaces)) if (hidden) effective[id] = true;
      // Inform service worker (for push suppression)
      if (navigator.serviceWorker && navigator.serviceWorker.controller) {
        navigator.serviceWorker.controller.postMessage({ type: 'SET_MUTED_SPACES', data: effective });
      }
    } catch { /* noop */ }
  }, [mutedSpaces, hiddenSpaces]);
  // Listen for external mutedSpaces updates (from settings modal)
  useEffect(() => {
    const onMuted = (e: Event) => {
      try {
        const ms = (e as CustomEvent<Record<string, boolean> | undefined>).detail;
        if (ms && typeof ms === 'object') setMutedSpaces(ms);
      } catch { /* noop */ }
    };
    window.addEventListener('echo:mutedSpaces', onMuted as EventListener);
    return () => window.removeEventListener('echo:mutedSpaces', onMuted as EventListener);
  }, []);
  return { mutedSpaces, setMutedSpaces };
}
